import styles from "./SelectPaymentMethodPopup.module.scss";
import { useTranslation } from "react-i18next";
import { useGetStripePaymentMethodsQuery, useGetUserSubscriptionQuery } from "../../api/queries";
import { IPaymentMethod, IStripePaymentResponse } from "../../models/Stripe";
import React, { useEffect, useState } from "react";
import plusIcon from "../../assets/images/icons/plus-orange.svg";
import CustomButton from "../ui/CustomButton/CustomButton";
import emptyIcon from "../../assets/images/icons/payment-empty.svg";
import AddNewPaymentMethodPopup from "../shared/AddNewPaymentMethodPopup/AddNewPaymentMethodPopup";
import { loadStripe } from "@stripe/stripe-js";
import { openErrorMessage } from "../../store/message/messageSlice";
import { handleError } from "../../helper/errorHandler";
import { useStripeMutations } from "../../api/mutations";
import PaymentResultPopup from "../shared/PaymentResultPopup/PaymentResultPopup";
import FullScreenLoader from "../shared/FullScreenLoader/FullScreenLoader";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";

interface ISelectPaymentMethodPopupProps {
  onClose: () => void;
  purchasingItemType: "addon" | "subscription";
  purchasingItemId: string;
  stripePublicKey: string;
}

const SelectPaymentMethodPopup: React.FC<ISelectPaymentMethodPopupProps> = ({
  onClose,
  purchasingItemType,
  purchasingItemId,
  stripePublicKey,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { buyAddon, buySubscription } = useStripeMutations();

  const { data, isLoading, refetch: refetchPaymentMethods } = useGetStripePaymentMethodsQuery();
  const paymentMethods = data?.paymentMethods || [];
  const user = useAppSelector((state) => state.userProfileSlice.user);
  const { refetch: refetchGetUserSubscription } = useGetUserSubscriptionQuery(user?.id as string);

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [showAddNewPopup, setShowAddNewPopup] = useState<boolean>(false);
  const [typeOfPaymentResultPopup, setTypeOfPaymentResultPopup] = useState<
    "success" | "failed" | null
  >(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (data?.paymentMethods?.length) {
      setSelectedPaymentMethodId(data.paymentMethods[0].id);
    }
  }, [data]);

  const clickOnPay = () => {
    if (purchasingItemType === "addon") {
      buyAddonFn();
    } else {
      buySubscriptionFn();
    }
  };

  const buySubscriptionFn = () => {
    setLoading(true);
    buySubscription(
      {
        paymentMethodId: selectedPaymentMethodId as string,
        subscriptionId: purchasingItemId,
      },
      {
        onSuccess,
        onError: (e) => {
          setLoading(false);
          setTypeOfPaymentResultPopup("failed");
          handleError(e, dispatch, t);
        },
      }
    );
  };

  const buyAddonFn = () => {
    setLoading(true);
    buyAddon(
      {
        paymentMethodId: selectedPaymentMethodId as string,
        addonId: purchasingItemId,
      },
      {
        onSuccess,
        onError: (e) => {
          setLoading(false);
          setTypeOfPaymentResultPopup("failed");
          handleError(e, dispatch, t);
        },
      }
    );
  };

  const onSuccess = async (res: IStripePaymentResponse) => {
    if (res.paymentCompleted) {
      setLoading(false);
      setTypeOfPaymentResultPopup("success");
      refetchGetUserSubscription();
    }

    if (res.clientSecret) {
      const stripeInstance = await loadStripe(stripePublicKey);
      if (!stripeInstance) {
        setTypeOfPaymentResultPopup("failed");
        setLoading(false);
        return;
      }

      const { error, paymentIntent } = await stripeInstance.confirmCardPayment(res.clientSecret, {
        payment_method: res.paymentMethodId,
      });

      if (error) {
        setTypeOfPaymentResultPopup("failed");
        setLoading(false);
        dispatch(openErrorMessage(error.message ?? "Payment failed"));
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        setTypeOfPaymentResultPopup("success");
        setLoading(false);
        refetchGetUserSubscription();
      } else {
        setTypeOfPaymentResultPopup("failed");
        setLoading(false);
      }
    }
  };

  const renderPaymentMethod = (paymentMethod: IPaymentMethod) => {
    return (
      <div
        className={styles.item}
        key={paymentMethod.id}
        onClick={() => {
          setSelectedPaymentMethodId(paymentMethod.id);
        }}
      >
        <img className={styles.cardBrand} src={paymentMethod.brandIconUrl} alt={"brand"} />
        <div className={styles.info}>
          <h3>
            {paymentMethod.brand} ***** {paymentMethod.last4}
          </h3>
          {paymentMethod.nameOnCard && <p className={styles.name}>{paymentMethod.nameOnCard}</p>}
          <p className={styles.exp}>
            EXP. {paymentMethod.expMonth}/{paymentMethod.expYear}
          </p>
        </div>
        <div className={styles.right}>
          {paymentMethod.isDefault && <span className={styles.default}>{t("Default")}</span>}
          <div
            className={`${styles.ring} ${selectedPaymentMethodId === paymentMethod.id && styles.active}`}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.popup}>
          <h2 className={styles.title}>{t("SelectPaymentMethod")}</h2>
          {!!paymentMethods.length && (
            <div className={styles.wrapper}>
              {paymentMethods.map((el: IPaymentMethod) => renderPaymentMethod(el))}
            </div>
          )}
          {!paymentMethods.length && !isLoading && (
            <div className={styles.emptyWrapper}>
              <img src={emptyIcon} alt={"empty"} />
              <h4>{t("YouHaveNoCardsAvailable")}</h4>
            </div>
          )}

          <span className={styles.addNew} onClick={() => setShowAddNewPopup(true)}>
            <img src={plusIcon} alt={"Add"} />
            {t("AddNewCard")}
          </span>

          <div className={styles.actions}>
            <CustomButton
              text={t("Cancel")}
              type={"button"}
              typeStyle={"outline"}
              functionOnClick={onClose}
            />
            <CustomButton
              text={t("Pay")}
              type={"button"}
              typeStyle={"fill"}
              disabled={!selectedPaymentMethodId}
              functionOnClick={clickOnPay}
            />
          </div>
        </div>
      </div>
      {showAddNewPopup && (
        <AddNewPaymentMethodPopup
          onClose={() => setShowAddNewPopup(false)}
          refetch={refetchPaymentMethods}
        />
      )}
      {typeOfPaymentResultPopup && (
        <PaymentResultPopup
          type={typeOfPaymentResultPopup}
          closeFn={() => {
            setTypeOfPaymentResultPopup(null);
            onClose();
          }}
        />
      )}
      {loading && <FullScreenLoader />}
    </>
  );
};

export default SelectPaymentMethodPopup;
